// src/components/HandwritingGenerator.tsx
import { useState as useState3, useEffect as useEffect2 } from "react";

// src/components/ui/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/ui/button.tsx
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-soft hover:shadow-card",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-gradient-accent text-accent-foreground hover:opacity-90 shadow-accent"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ui/popover.tsx
import * as React2 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx2 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverContent = React2.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx2(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx2(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/components/ui/textarea.tsx
import * as React3 from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Textarea = React3.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx3(
      "textarea",
      {
        className: cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/components/ui/slider.tsx
import * as React4 from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var Slider = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(
  SliderPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex w-full touch-none select-none items-center",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx4(SliderPrimitive.Track, { className: "relative h-2 w-full grow overflow-hidden rounded-full bg-secondary", children: /* @__PURE__ */ jsx4(SliderPrimitive.Range, { className: "absolute h-full bg-primary" }) }),
      /* @__PURE__ */ jsx4(SliderPrimitive.Thumb, { className: "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" })
    ]
  }
));
Slider.displayName = SliderPrimitive.Root.displayName;

// src/components/ui/switch.tsx
import * as React5 from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { jsx as jsx5 } from "react/jsx-runtime";
var Switch = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx5(
  SwitchPrimitives.Root,
  {
    className: cn(
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx5(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;

// src/components/ui/label.tsx
import * as React6 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx6 } from "react/jsx-runtime";
var labelVariants = cva2(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx6(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;

// src/components/HandwritingGenerator.tsx
import { Upload as Upload2, Download as Download2, Type, ChevronDown as ChevronDown3, FileText, Check as Check3, RotateCcw as RotateCcw2, ExternalLink, Lock } from "lucide-react";

// src/components/TextRecognition.tsx
import { useCallback, useState } from "react";

// src/components/ui/card.tsx
import * as React7 from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var Card = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  "h3",
  {
    ref,
    className: cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";

// src/components/TextRecognition.tsx
import { Upload, Camera, AlertCircle } from "lucide-react";

// src/components/ui/alert.tsx
import * as React8 from "react";
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx8 } from "react/jsx-runtime";
var alertVariants = cva3(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Alert = React8.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx8(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
var AlertTitle = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  "h5",
  {
    ref,
    className: cn("mb-1 font-medium leading-none tracking-tight", className),
    ...props
  }
));
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  "div",
  {
    ref,
    className: cn("text-sm [&_p]:leading-relaxed", className),
    ...props
  }
));
AlertDescription.displayName = "AlertDescription";

// src/components/TextRecognition.tsx
import { jsx as jsx9, jsxs as jsxs2 } from "react/jsx-runtime";
function TextRecognition({ onTextExtracted, onFileUpload, texts }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    if (!onFileUpload) {
      console.warn("onFileUpload handler not provided");
      return;
    }
    setIsProcessing(true);
    try {
      const recognizedText = await onFileUpload(files);
      onTextExtracted(recognizedText);
    } catch (error) {
      console.error("Error processing image:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [onTextExtracted, onFileUpload]);
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [handleFileUpload]);
  const handleFileSelect = useCallback((e) => {
    handleFileUpload(e.target.files);
  }, [handleFileUpload]);
  return /* @__PURE__ */ jsxs2("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs2("div", { children: [
      /* @__PURE__ */ jsx9("h3", { className: "text-lg font-semibold mb-2", children: texts.title }),
      /* @__PURE__ */ jsx9("p", { className: "text-sm text-muted-foreground mb-4", children: texts.description })
    ] }),
    /* @__PURE__ */ jsxs2(Alert, { children: [
      /* @__PURE__ */ jsx9(AlertCircle, { className: "h-4 w-4" }),
      /* @__PURE__ */ jsxs2(AlertDescription, { children: [
        /* @__PURE__ */ jsx9("strong", { children: texts.warning }),
        " ",
        texts.warningText
      ] })
    ] }),
    /* @__PURE__ */ jsx9(
      Card,
      {
        className: `relative border-2 border-dashed transition-all duration-200 ${dragActive ? "border-accent bg-accent/5" : "border-border hover:border-accent/50"}`,
        onDragEnter: handleDrag,
        onDragLeave: handleDrag,
        onDragOver: handleDrag,
        onDrop: handleDrop,
        children: /* @__PURE__ */ jsxs2("div", { className: "py-8 px-4 text-center", children: [
          /* @__PURE__ */ jsx9("div", { className: "w-16 h-16 mx-auto mb-4 bg-gradient-primary rounded-full flex items-center justify-center", children: isProcessing ? /* @__PURE__ */ jsx9("div", { className: "w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" }) : /* @__PURE__ */ jsx9(Camera, { className: "w-8 h-8 text-white" }) }),
          /* @__PURE__ */ jsx9("h3", { className: "text-lg font-semibold mb-2", children: isProcessing ? texts.processing : texts.uploadTitle }),
          /* @__PURE__ */ jsx9("p", { className: "text-muted-foreground mb-6", children: isProcessing ? texts.processingWait : texts.dragDrop }),
          /* @__PURE__ */ jsx9(
            "input",
            {
              type: "file",
              id: "file-upload",
              className: "hidden",
              multiple: true,
              accept: "image/*",
              onChange: handleFileSelect,
              disabled: isProcessing
            }
          ),
          !isProcessing && /* @__PURE__ */ jsx9(
            Button,
            {
              asChild: true,
              className: "bg-gradient-accent hover:bg-accent shadow-accent",
              children: /* @__PURE__ */ jsxs2("label", { htmlFor: "file-upload", className: "cursor-pointer", children: [
                /* @__PURE__ */ jsx9(Upload, { className: "w-4 h-4 mr-2" }),
                texts.selectFiles
              ] })
            }
          )
        ] })
      }
    )
  ] });
}

// src/components/PreviewArea.tsx
import { Download } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs3 } from "react/jsx-runtime";
function PreviewArea({ text, textConfig, backgroundConfig, PreviewComponent, texts }) {
  return /* @__PURE__ */ jsx10("div", { className: "space-y-4", children: /* @__PURE__ */ jsxs3(
    "div",
    {
      className: "flex justify-center p-4 lg:p-8 relative bg-white rounded-lg shadow-soft overflow-hidden",
      children: [
        !text?.length && /* @__PURE__ */ jsx10("div", { className: "inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm min-h-[300px] lg:min-h-[calc(100vh-8rem-109px)]", children: /* @__PURE__ */ jsxs3("div", { className: "text-center animate-fade-in px-4", children: [
          /* @__PURE__ */ jsx10("div", { className: "w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-4 bg-gradient-primary rounded-full flex items-center justify-center animate-float", children: /* @__PURE__ */ jsx10(Download, { className: "w-6 h-6 lg:w-8 lg:h-8 text-white" }) }),
          /* @__PURE__ */ jsx10("p", { className: "text-muted-foreground text-sm lg:text-base", children: texts.placeholder })
        ] }) }),
        text && PreviewComponent && /* @__PURE__ */ jsx10(PreviewComponent, { text })
      ]
    }
  ) });
}

// src/components/AiChatInterface.tsx
import React12, { useState as useState2, useRef, useEffect } from "react";

// src/components/ui/select.tsx
import * as React10 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx11(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx11(ChevronDown, { className: "h-4 w-4 opacity-50" }) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx11(ChevronUp, { className: "h-4 w-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx11(ChevronDown, { className: "h-4 w-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React10.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx11(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs4(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx11(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx11(
        SelectPrimitive.Viewport,
        {
          className: cn(
            "p-1",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx11(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx11("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx11(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx11(Check, { className: "h-4 w-4" }) }) }),
      /* @__PURE__ */ jsx11(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/components/ui/dialog.tsx
import * as React11 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { jsx as jsx12, jsxs as jsxs5 } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogPortal = DialogPrimitive.Portal;
var DialogOverlay = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React11.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs5(DialogPortal, { children: [
  /* @__PURE__ */ jsx12(DialogOverlay, {}),
  /* @__PURE__ */ jsxs5(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs5(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground", children: [
          /* @__PURE__ */ jsx12(X, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx12("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx12(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx12(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/components/AiChatInterface.tsx
import { Send, Loader2, Copy, Check as Check2, ChevronDown as ChevronDown2, ChevronUp as ChevronUp2, Maximize2 } from "lucide-react";
import { Fragment, jsx as jsx13, jsxs as jsxs6 } from "react/jsx-runtime";
function AiChatInterface({
  messages,
  isProcessing,
  availableTokens,
  aiActions,
  onSendMessage,
  className = "",
  texts,
  isFullscreen: isFullscreenProp,
  onSetFullscreen
}) {
  const [inputText, setInputText] = useState2("");
  const [selectedAction, setSelectedAction] = useState2(aiActions[0]?.id || "");
  const [copiedMessageId, setCopiedMessageId] = useState2(null);
  const [expandedMessages, setExpandedMessages] = useState2(/* @__PURE__ */ new Set());
  const [isFullscreenInternal, setIsFullscreenInternal] = useState2(false);
  const isFullscreen = isFullscreenProp ?? isFullscreenInternal;
  const setIsFullscreen = (v) => {
    onSetFullscreen ? onSetFullscreen(v) : setIsFullscreenInternal(v);
  };
  const messagesEndRef = useRef(null);
  const fullscreenMessagesEndRef = useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    fullscreenMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing]);
  useEffect(() => {
    if (isFullscreen) {
      setTimeout(() => fullscreenMessagesEndRef.current?.scrollIntoView({ behavior: "instant" }), 50);
    }
  }, [isFullscreen]);
  const handleSend = () => {
    if (inputText.trim() && selectedAction && !isProcessing) {
      onSendMessage(inputText.trim(), selectedAction);
      setInputText("");
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2e3);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };
  const toggleMessageExpansion = (messageId) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };
  const isMessageLong = (text) => text.length > 200;
  const getTruncatedText = (text) => text.substring(0, 200) + "...";
  const shouldShowText = (message) => {
    return !isMessageLong(message.text) || expandedMessages.has(message.id);
  };
  const selectedActionData = aiActions.find((a) => a.id === selectedAction);
  const renderMessages = (endRef, expandAll = false) => /* @__PURE__ */ jsxs6("div", { className: cn("flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth", { "flex items-center justify-center": messages.length === 0 }), children: [
    messages.length === 0 && !isProcessing ? /* @__PURE__ */ jsxs6("div", { className: "text-center text-gray-500 py-8", children: [
      /* @__PURE__ */ jsx13("div", { className: "mb-2", children: "\u{1F4AC}" }),
      /* @__PURE__ */ jsx13("p", { className: "text-sm", children: texts.emptyState.split("\n").map((line, i) => /* @__PURE__ */ jsxs6(React12.Fragment, { children: [
        line,
        i < texts.emptyState.split("\n").length - 1 && /* @__PURE__ */ jsx13("br", {})
      ] }, i)) })
    ] }) : messages.map((message) => /* @__PURE__ */ jsx13(
      "div",
      {
        className: `flex ${message.isUser ? "justify-end" : "justify-start"}`,
        children: /* @__PURE__ */ jsxs6(
          "div",
          {
            className: `max-w-[80%] rounded-lg p-3 relative group ${message.isUser ? "bg-gradient-accent text-white" : "bg-gray-100 text-gray-900"}`,
            children: [
              /* @__PURE__ */ jsx13(
                "button",
                {
                  onClick: () => copyToClipboard(message.text, message.id),
                  className: "absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-white hover:bg-gray-50 text-gray-600 shadow-sm border",
                  title: texts.copy,
                  children: copiedMessageId === message.id ? /* @__PURE__ */ jsx13(Check2, { className: "w-3 h-3" }) : /* @__PURE__ */ jsx13(Copy, { className: "w-3 h-3" })
                }
              ),
              message.action && message.isUser && /* @__PURE__ */ jsx13("div", { className: "text-xs opacity-75 mb-1 font-semibold", children: texts.getAction(aiActions.find((a) => a.id === message.action)?.name ?? "") }),
              /* @__PURE__ */ jsx13("div", { className: "whitespace-pre-wrap text-sm pr-2", children: expandAll || shouldShowText(message) ? message.text : getTruncatedText(message.text) }),
              !expandAll && isMessageLong(message.text) && /* @__PURE__ */ jsx13(
                "button",
                {
                  onClick: () => toggleMessageExpansion(message.id),
                  className: "text-xs text-blue-500 hover:text-blue-700 mt-1 flex items-center gap-1",
                  children: expandedMessages.has(message.id) ? /* @__PURE__ */ jsxs6(Fragment, { children: [
                    texts.collapse,
                    " ",
                    /* @__PURE__ */ jsx13(ChevronUp2, { className: "w-3 h-3" })
                  ] }) : /* @__PURE__ */ jsxs6(Fragment, { children: [
                    texts.expand,
                    " ",
                    /* @__PURE__ */ jsx13(ChevronDown2, { className: "w-3 h-3" })
                  ] })
                }
              ),
              /* @__PURE__ */ jsx13("div", { className: "text-xs opacity-75 mt-1 font-semibold", children: message.timestamp.toLocaleString(texts.locale, {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              }) })
            ]
          }
        )
      },
      message.id
    )),
    isProcessing && /* @__PURE__ */ jsx13("div", { className: "flex justify-start", children: /* @__PURE__ */ jsxs6("div", { className: "bg-gray-100 rounded-lg p-3 flex items-center gap-2", children: [
      /* @__PURE__ */ jsx13(Loader2, { className: "w-4 h-4 animate-spin" }),
      /* @__PURE__ */ jsx13("span", { className: "text-sm text-gray-600", children: texts.thinking })
    ] }) }),
    /* @__PURE__ */ jsx13("div", { className: "!m-0", ref: endRef })
  ] });
  const renderInput = (tall = false) => /* @__PURE__ */ jsxs6("div", { className: "p-4 border-t border-border space-y-2 flex-shrink-0", children: [
    /* @__PURE__ */ jsxs6(Select, { value: selectedAction, onValueChange: setSelectedAction, children: [
      /* @__PURE__ */ jsx13(SelectTrigger, { className: "w-full", children: /* @__PURE__ */ jsx13(SelectValue, { placeholder: texts.actionPlaceholder }) }),
      /* @__PURE__ */ jsx13(SelectContent, { children: aiActions.map((action) => /* @__PURE__ */ jsx13(SelectItem, { value: action.id, children: action.name }, action.id)) })
    ] }),
    /* @__PURE__ */ jsxs6("div", { className: "flex gap-2 relative", children: [
      /* @__PURE__ */ jsx13(
        Textarea,
        {
          placeholder: texts.inputPlaceholder,
          value: inputText,
          onChange: (e) => setInputText(e.target.value),
          onKeyPress: handleKeyPress,
          className: `flex-1 resize-none pr-[40px] ${tall ? "h-[150px]" : "min-h-[60px] max-h-[120px]"}`,
          disabled: isProcessing
        }
      ),
      /* @__PURE__ */ jsx13(
        Button,
        {
          onClick: handleSend,
          disabled: !inputText.trim() || !selectedAction || isProcessing,
          size: "sm",
          className: "self-end bg-gradient-accent hover:bg-accent !rounded-full absolute top-0 right-2 bottom-0 w-[40px] h-[40px] m-auto",
          children: isProcessing ? /* @__PURE__ */ jsx13(Loader2, { className: "w-4 h-4 animate-spin" }) : /* @__PURE__ */ jsx13(Send, { className: "w-4 h-4" })
        }
      )
    ] }),
    selectedActionData?.description && /* @__PURE__ */ jsx13("div", { className: "text-xs text-muted-foreground", children: selectedActionData.description })
  ] });
  const renderHeader = (showExpand = true) => /* @__PURE__ */ jsxs6("div", { className: "px-4 pt-4 pb-3 border-b border-border flex-shrink-0", children: [
    /* @__PURE__ */ jsxs6("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx13("h4", { className: "font-medium", children: texts.title }),
      showExpand && /* @__PURE__ */ jsx13(
        "button",
        {
          onClick: () => setIsFullscreen(true),
          className: "text-muted-foreground hover:text-foreground transition-colors",
          title: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043D\u0430 \u0432\u0435\u0441\u044C \u044D\u043A\u0440\u0430\u043D",
          children: /* @__PURE__ */ jsx13(Maximize2, { className: "w-4 h-4" })
        }
      )
    ] }),
    availableTokens !== void 0 && /* @__PURE__ */ jsx13("span", { className: "text-xs text-muted-foreground mt-0.5 block", children: texts.getTokensAvailable(availableTokens) })
  ] });
  return /* @__PURE__ */ jsxs6(Fragment, { children: [
    /* @__PURE__ */ jsxs6("div", { className: `flex flex-col h-full ${className}`, children: [
      renderHeader(),
      renderMessages(messagesEndRef),
      renderInput()
    ] }),
    /* @__PURE__ */ jsx13(Dialog, { open: isFullscreen, onOpenChange: setIsFullscreen, children: /* @__PURE__ */ jsxs6(DialogContent, { className: "max-w-4xl w-[90vw] h-[90vh] flex flex-col p-0 gap-0", children: [
      renderHeader(false),
      renderMessages(fullscreenMessagesEndRef, true),
      renderInput(true)
    ] }) })
  ] });
}

// src/components/HandwritingGenerator.tsx
import { jsx as jsx14, jsxs as jsxs7 } from "react/jsx-runtime";
var defaultTextConfig = {
  font: "Abram",
  fontSize: 31,
  lineHeight: 35,
  letterSpacing: 0,
  wordSpacing: 0,
  rightMargin: 0,
  leftMargin: 8,
  topMargin: 5,
  indentSize: 75,
  color: "black",
  randomSize: false,
  randomSlant: false,
  randomSpacing: false
};
var defaultBackgroundConfig = {
  type: "default",
  paperSize: "A4",
  backgroundType: "lines",
  doublePage: false,
  image: null
};
var defaultTextSettingsLimits = {
  fontSize: { min: 12, max: 60, step: 1 },
  lineHeight: { min: 20, max: 80, step: 1 },
  letterSpacing: { min: -5, max: 20, step: 1 },
  wordSpacing: { min: -10, max: 30, step: 1 },
  leftMargin: { min: 0, max: 100, step: 1 },
  rightMargin: { min: 0, max: 100, step: 1 },
  topMargin: { min: 0, max: 50, step: 1 },
  indentSize: { min: 0, max: 150, step: 5 }
};
var defaultAvailableFonts = [
  "Abram",
  "Anselmo",
  "Benvolio",
  "Capuletty",
  "Djiovanno",
  "Eskal",
  "Gregory",
  "Lorenco",
  "Merkucio",
  "Montekky"
];
function HandwritingGenerator({
  initialTextConfig,
  initialBackgroundConfig,
  initialText = "",
  onTextConfigChange,
  onBackgroundConfigChange,
  onTextChange,
  PreviewComponent,
  textSettingsLimits,
  availableFonts,
  onCustomFontLoad,
  onResetTextSettings,
  onResetBackgroundSettings,
  customFontStorageKey = "customFontData",
  onCustomBackgroundLoad,
  customBackgroundStorageKey = "customBackgroundData",
  onDownloadNormal,
  onDownloadHighRes,
  onDownloadPDF,
  isAuthenticated = false,
  hasSubscription = false,
  hasDoublePage = false,
  hasA4Format = false,
  onShowAuthModal,
  onShowSubscriptionModal,
  onTextRecognition,
  leftBanner,
  rightBanner,
  topBanner,
  showNewFeatures = false,
  aiActions = [],
  onAIProcess,
  aiTokens = 0,
  showAIFeatures = false,
  isAIProcessing = false,
  chatMessages = [],
  onSendChatMessage,
  texts
}) {
  const [textConfig, setTextConfig] = useState3({
    ...defaultTextConfig,
    ...initialTextConfig
  });
  const [backgroundConfig, setBackgroundConfig] = useState3({
    ...defaultBackgroundConfig,
    ...initialBackgroundConfig
  });
  const [inputText, setInputText] = useState3(initialText);
  const [isCustomBackgroundLoaded, setIsCustomBackgroundLoaded] = useState3(false);
  const [showRecognitionModal, setShowRecognitionModal] = useState3(false);
  const [isAiChatFullscreen, setIsAiChatFullscreen] = useState3(false);
  const [selectedAIAction, setSelectedAIAction] = useState3(aiActions[0]?.id || "");
  const [activeTab, setActiveTab] = useState3("text");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState3(false);
  const limits = {
    ...defaultTextSettingsLimits,
    ...textSettingsLimits
  };
  const fonts = availableFonts || defaultAvailableFonts;
  const handlePDFClick = () => {
    if (!isAuthenticated) {
      onShowAuthModal?.();
      return;
    }
    if (!hasSubscription) {
      setIsAiChatFullscreen(false);
      onShowSubscriptionModal?.("premium");
      return;
    }
    onDownloadPDF?.();
  };
  const handleDoublePageChange = (checked) => {
    if (checked) {
      if (!isAuthenticated) {
        onShowAuthModal?.();
        return;
      }
      if (!hasDoublePage) {
        setIsAiChatFullscreen(false);
        onShowSubscriptionModal?.("premium");
        return;
      }
    }
    handleBackgroundConfigChange({ doublePage: checked });
  };
  const handleA4Click = () => {
    if (!isAuthenticated) {
      onShowAuthModal?.();
      return;
    }
    if (!hasA4Format) {
      setIsAiChatFullscreen(false);
      onShowSubscriptionModal?.("premium");
      return;
    }
    handleBackgroundConfigChange({ paperSize: "A4" });
  };
  const getFontDisplayName = (fontName) => {
    return fontName === "customFont" ? texts.customFontName : fontName;
  };
  const saveCustomFontToStorage = (fontData) => {
    try {
      localStorage.setItem(customFontStorageKey, fontData);
    } catch (error) {
      console.error("Failed to save custom font to localStorage:", error);
    }
  };
  const loadCustomFontFromStorage = () => {
    try {
      return localStorage.getItem(customFontStorageKey);
    } catch (error) {
      console.error("Failed to load custom font from localStorage:", error);
      return null;
    }
  };
  const loadFontFromData = async (fontData) => {
    try {
      const style = document.createElement("style");
      style.innerHTML = `
        @font-face {
          font-family: customFont;
          src: url('${fontData}')
        }
      `;
      document.head.append(style);
      if (document.fonts) {
        await document.fonts.load("10px customFont");
        console.log("Custom font loaded from storage");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load custom font:", error);
      return false;
    }
  };
  const saveCustomBackgroundToStorage = (backgroundData) => {
    try {
      localStorage.setItem(customBackgroundStorageKey, backgroundData);
    } catch (error) {
      console.error("Failed to save custom background to localStorage:", error);
    }
  };
  const loadCustomBackgroundFromStorage = () => {
    try {
      return localStorage.getItem(customBackgroundStorageKey);
    } catch (error) {
      console.error("Failed to load custom background from localStorage:", error);
      return null;
    }
  };
  const loadBackgroundFromData = (backgroundData) => {
    try {
      if (backgroundData.startsWith("data:image/")) {
        console.log("Custom background loaded from storage");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load custom background:", error);
      return false;
    }
  };
  useEffect2(() => {
    if (initialTextConfig) {
      setTextConfig((prev) => ({ ...prev, ...initialTextConfig }));
    }
  }, [initialTextConfig]);
  useEffect2(() => {
    if (initialBackgroundConfig) {
      setBackgroundConfig((prev) => ({ ...prev, ...initialBackgroundConfig }));
    }
  }, [initialBackgroundConfig]);
  useEffect2(() => {
    if (initialText !== void 0) {
      setInputText(initialText);
    }
  }, [initialText]);
  useEffect2(() => {
    const restoreCustomFont = async () => {
      const savedFontData = loadCustomFontFromStorage();
      if (savedFontData) {
        const loaded = await loadFontFromData(savedFontData);
        if (loaded) {
          onCustomFontLoad?.("customFont");
        }
      }
    };
    restoreCustomFont();
  }, []);
  useEffect2(() => {
    const restoreCustomBackground = () => {
      const savedBackgroundData = loadCustomBackgroundFromStorage();
      if (savedBackgroundData) {
        const loaded = loadBackgroundFromData(savedBackgroundData);
        if (loaded) {
          setIsCustomBackgroundLoaded(true);
          onCustomBackgroundLoad?.("customBackground");
        }
      }
    };
    restoreCustomBackground();
  }, []);
  const handleTextConfigChange = (newConfig) => {
    const updatedConfig = { ...textConfig, ...newConfig };
    setTextConfig(updatedConfig);
    onTextConfigChange?.(updatedConfig);
  };
  const handleBackgroundConfigChange = (newConfig) => {
    const updatedConfig = { ...backgroundConfig, ...newConfig };
    setBackgroundConfig(updatedConfig);
    onBackgroundConfigChange?.(updatedConfig);
  };
  const handleTextChange = (newText) => {
    setInputText(newText);
    onTextChange?.(newText);
  };
  const handleDownload = () => {
    console.log("Downloading handwritten note...");
  };
  const handleAIProcess = () => {
    if (!selectedAIAction || !inputText.trim() || !onAIProcess) return;
    onAIProcess(selectedAIAction, inputText);
  };
  useEffect2(() => {
    if (aiActions.length > 0 && !selectedAIAction) {
      setSelectedAIAction(aiActions[0].id);
    }
  }, [aiActions, selectedAIAction]);
  const BACKGROUND_PREVIEWS = {
    A4: {
      grid: "/api/placeholder/120/80?text=\u041A\u043B\u0435\u0442\u043A\u0430+A4",
      lines: "/api/placeholder/120/80?text=\u041B\u0438\u043D\u0438\u0438+A4",
      blank: "/api/placeholder/120/80?text=\u0427\u0438\u0441\u0442\u044B\u0439+A4"
    },
    A5: {
      grid: "/api/placeholder/120/80?text=\u041A\u043B\u0435\u0442\u043A\u0430+A5",
      lines: "/api/placeholder/120/80?text=\u041B\u0438\u043D\u0438\u0438+A5",
      blank: "/api/placeholder/120/80?text=\u0427\u0438\u0441\u0442\u044B\u0439+A5"
    }
  };
  return /* @__PURE__ */ jsxs7("div", { className: "bg-background", children: [
    /* @__PURE__ */ jsx14("div", { className: "sticky top-[60px] z-50 bg-white border-b border-border shadow-sm", children: /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-between gap-2 px-2 lg:px-4 py-2 overflow-x-auto", children: [
      /* @__PURE__ */ jsxs7("div", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsxs7(Popover, { children: [
          /* @__PURE__ */ jsx14(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs7(Button, { variant: "outline", size: "sm", className: "h-10 lg:h-8 gap-1 px-3 lg:px-2", children: [
            /* @__PURE__ */ jsx14(Type, { className: "w-5 h-5 lg:w-4 lg:h-4" }),
            /* @__PURE__ */ jsx14("span", { className: "hidden lg:inline", children: texts.textSettings }),
            /* @__PURE__ */ jsx14(ChevronDown3, { className: "w-4 h-4 lg:w-3 lg:h-3" })
          ] }) }),
          /* @__PURE__ */ jsx14(PopoverContent, { className: "w-[90vw] max-w-md lg:w-72 z-50 bg-white", children: /* @__PURE__ */ jsxs7("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsx14("h4", { className: "font-medium", children: texts.textSettings }),
              /* @__PURE__ */ jsxs7(
                Button,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onResetTextSettings,
                  className: "h-6 text-xs text-destructive hover:text-accent-foreground",
                  disabled: !onResetTextSettings,
                  children: [
                    /* @__PURE__ */ jsx14(RotateCcw2, { className: "w-3 h-3" }),
                    texts.reset
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs7("div", { className: "space-y-3", children: [
              /* @__PURE__ */ jsxs7("div", { children: [
                /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium", children: texts.font }),
                /* @__PURE__ */ jsx14(
                  "select",
                  {
                    value: textConfig.font,
                    onChange: (e) => handleTextConfigChange({ font: e.target.value }),
                    className: "w-full h-10 lg:h-8 px-2 border border-border rounded text-sm lg:text-sm text-base",
                    children: fonts.map((font) => /* @__PURE__ */ jsx14("option", { value: font, children: getFontDisplayName(font) }, font))
                  }
                ),
                /* @__PURE__ */ jsx14("div", { className: "text-center", children: /* @__PURE__ */ jsx14("span", { className: "text-xs text-muted-foreground", children: texts.or }) }),
                /* @__PURE__ */ jsxs7(
                  Button,
                  {
                    variant: "outline",
                    size: "sm",
                    className: "w-full h-10 lg:h-8 mt-1",
                    onClick: () => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = ".ttf,.otf,.woff,.woff2";
                      input.onchange = async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = async () => {
                            const fontData = reader.result;
                            const loaded = await loadFontFromData(fontData);
                            if (loaded) {
                              saveCustomFontToStorage(fontData);
                              onCustomFontLoad?.("customFont");
                              handleTextConfigChange({ font: "customFont" });
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      };
                      input.click();
                    },
                    children: [
                      /* @__PURE__ */ jsx14(Upload2, { className: "w-3 h-3 mr-1" }),
                      texts.uploadFont
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs7("div", { children: [
                /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium mb-2 block", children: texts.getFontSize(textConfig.fontSize) }),
                /* @__PURE__ */ jsx14(
                  Slider,
                  {
                    value: [textConfig.fontSize],
                    onValueChange: ([value]) => handleTextConfigChange({ fontSize: value }),
                    min: limits.fontSize.min,
                    max: limits.fontSize.max,
                    step: limits.fontSize.step,
                    className: "w-full"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs7("div", { children: [
                /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium mb-2 block", children: texts.getLineHeight(textConfig.lineHeight) }),
                /* @__PURE__ */ jsx14(
                  Slider,
                  {
                    value: [textConfig.lineHeight],
                    onValueChange: ([value]) => handleTextConfigChange({ lineHeight: value }),
                    min: limits.lineHeight.min,
                    max: limits.lineHeight.max,
                    step: limits.lineHeight.step,
                    className: "w-full"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs7("div", { children: [
                /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium mb-2 block", children: texts.getLetterSpacing(textConfig.letterSpacing) }),
                /* @__PURE__ */ jsx14(
                  Slider,
                  {
                    value: [textConfig.letterSpacing],
                    onValueChange: ([value]) => handleTextConfigChange({ letterSpacing: value }),
                    min: limits.letterSpacing.min,
                    max: limits.letterSpacing.max,
                    step: limits.letterSpacing.step,
                    className: "w-full"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs7("div", { children: [
                /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium", children: texts.textColor }),
                /* @__PURE__ */ jsx14("div", { className: "grid grid-cols-3 gap-1 mt-1", children: texts.colors.map((color) => /* @__PURE__ */ jsxs7(
                  Button,
                  {
                    variant: "outline",
                    size: "sm",
                    onClick: () => handleTextConfigChange({ color: color.value }),
                    className: "h-6 p-1 relative",
                    children: [
                      /* @__PURE__ */ jsx14(
                        "div",
                        {
                          className: "w-full h-full rounded",
                          style: { backgroundColor: color.value }
                        }
                      ),
                      textConfig.color === color.value && /* @__PURE__ */ jsx14(Check3, { className: "absolute inset-0 m-auto w-3 h-3 text-white drop-shadow-sm" })
                    ]
                  },
                  color.value
                )) }),
                /* @__PURE__ */ jsx14("div", { className: "mt-2", children: /* @__PURE__ */ jsx14(
                  "input",
                  {
                    type: "color",
                    value: textConfig.color,
                    onChange: (e) => handleTextConfigChange({ color: e.target.value }),
                    className: "w-full h-6 rounded border border-border cursor-pointer"
                  }
                ) })
              ] })
            ] })
          ] }) })
        ] }),
        /* @__PURE__ */ jsxs7(Popover, { children: [
          /* @__PURE__ */ jsx14(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs7(Button, { variant: "outline", size: "sm", className: "h-10 lg:h-8 gap-1 px-3 lg:px-2", children: [
            /* @__PURE__ */ jsx14(FileText, { className: "w-5 h-5 lg:w-4 lg:h-4" }),
            /* @__PURE__ */ jsx14("span", { className: "hidden lg:inline", children: texts.bgSettings }),
            /* @__PURE__ */ jsx14(ChevronDown3, { className: "w-4 h-4 lg:w-3 lg:h-3" })
          ] }) }),
          /* @__PURE__ */ jsx14(PopoverContent, { className: "w-[90vw] max-w-lg lg:w-80 z-50 bg-white", children: /* @__PURE__ */ jsxs7("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsx14("h4", { className: "font-medium", children: texts.bgSettings }),
              /* @__PURE__ */ jsxs7(
                Button,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onResetBackgroundSettings,
                  className: "h-6 text-xs text-destructive hover:text-accent-foreground",
                  disabled: !onResetBackgroundSettings,
                  children: [
                    /* @__PURE__ */ jsx14(RotateCcw2, { className: "w-3 h-3" }),
                    texts.reset
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs7("div", { className: "space-y-3", children: [
              showNewFeatures && /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-between", children: [
                /* @__PURE__ */ jsxs7("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsxs7(Label, { className: "text-sm font-medium flex items-center gap-2", children: [
                    texts.doublePage,
                    (!isAuthenticated || !hasDoublePage) && /* @__PURE__ */ jsx14(Lock, { size: 12, className: "text-gray-500" })
                  ] }),
                  /* @__PURE__ */ jsx14("span", { className: "bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-medium", children: "NEW" })
                ] }),
                /* @__PURE__ */ jsx14(
                  Switch,
                  {
                    checked: backgroundConfig.doublePage,
                    onCheckedChange: handleDoublePageChange
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs7("div", { children: [
                /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium", children: texts.bgType }),
                /* @__PURE__ */ jsx14("div", { className: "grid grid-cols-3 gap-2", children: (isCustomBackgroundLoaded ? ["grid", "lines", "blank", "custom"] : ["grid", "lines", "blank"]).map((bgType) => /* @__PURE__ */ jsx14("div", { className: "text-center", children: /* @__PURE__ */ jsx14(
                  Button,
                  {
                    variant: backgroundConfig.backgroundType === bgType ? "default" : "outline",
                    size: "sm",
                    onClick: () => handleBackgroundConfigChange({ backgroundType: bgType }),
                    className: "w-full h-16 p-1 flex flex-col gap-1",
                    children: /* @__PURE__ */ jsx14("div", { className: "flex-1 w-full bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center", children: /* @__PURE__ */ jsx14("span", { className: "text-xs text-gray-500", children: bgType === "grid" ? texts.bgTypes.grid : bgType === "lines" ? texts.bgTypes.lines : bgType === "blank" ? texts.bgTypes.blank : texts.bgTypes.custom }) })
                  }
                ) }, bgType)) }),
                /* @__PURE__ */ jsx14("div", { className: "text-center", children: /* @__PURE__ */ jsx14("span", { className: "text-xs text-muted-foreground", children: texts.or }) }),
                /* @__PURE__ */ jsxs7(
                  Button,
                  {
                    variant: "outline",
                    size: "sm",
                    className: "w-full h-10 lg:h-8 mt-1",
                    onClick: () => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = ".jpg,.jpeg,.png,.gif,.bmp,.webp";
                      input.onchange = (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const backgroundData = reader.result;
                            const loaded = loadBackgroundFromData(backgroundData);
                            if (loaded) {
                              saveCustomBackgroundToStorage(backgroundData);
                              setIsCustomBackgroundLoaded(true);
                              onCustomBackgroundLoad?.("customBackground");
                              handleBackgroundConfigChange({ backgroundType: "custom" });
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      };
                      input.click();
                    },
                    children: [
                      /* @__PURE__ */ jsx14(Upload2, { className: "w-3 h-3 mr-1" }),
                      texts.uploadBg
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsx14("div", { children: /* @__PURE__ */ jsxs7("div", { className: "space-y-3 mt-2", children: [
                /* @__PURE__ */ jsxs7("div", { children: [
                  /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium mb-2 block", children: texts.getMarginLeft(textConfig.leftMargin) }),
                  /* @__PURE__ */ jsx14(
                    Slider,
                    {
                      value: [textConfig.leftMargin],
                      onValueChange: ([value]) => handleTextConfigChange({ leftMargin: value }),
                      min: limits.leftMargin.min,
                      max: limits.leftMargin.max,
                      step: limits.leftMargin.step,
                      className: "w-full"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs7("div", { children: [
                  /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium mb-2 block", children: texts.getMarginRight(textConfig.rightMargin) }),
                  /* @__PURE__ */ jsx14(
                    Slider,
                    {
                      value: [textConfig.rightMargin],
                      onValueChange: ([value]) => handleTextConfigChange({ rightMargin: value }),
                      min: limits.rightMargin.min,
                      max: limits.rightMargin.max,
                      step: limits.rightMargin.step,
                      className: "w-full"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs7("div", { children: [
                  /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium mb-2 block", children: texts.getMarginTop(textConfig.topMargin) }),
                  /* @__PURE__ */ jsx14(
                    Slider,
                    {
                      value: [textConfig.topMargin],
                      onValueChange: ([value]) => handleTextConfigChange({ topMargin: value }),
                      min: limits.topMargin.min,
                      max: limits.topMargin.max,
                      step: limits.topMargin.step,
                      className: "w-full"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs7("div", { children: [
                  /* @__PURE__ */ jsx14(Label, { className: "text-sm font-medium mb-2 block", children: texts.getRedLine(textConfig.indentSize) }),
                  /* @__PURE__ */ jsx14(
                    Slider,
                    {
                      value: [textConfig.indentSize],
                      onValueChange: ([value]) => handleTextConfigChange({ indentSize: value }),
                      min: limits.indentSize.min,
                      max: limits.indentSize.max,
                      step: limits.indentSize.step,
                      className: "w-full"
                    }
                  )
                ] })
              ] }) })
            ] })
          ] }) })
        ] })
      ] }),
      /* @__PURE__ */ jsx14("div", { className: "flex-1" }),
      /* @__PURE__ */ jsx14("div", { className: "flex items-center", children: /* @__PURE__ */ jsxs7(
        "a",
        {
          href: "/old/",
          className: "flex items-center gap-1 px-3 lg:px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors",
          children: [
            /* @__PURE__ */ jsx14(ExternalLink, { className: "w-4 h-4" }),
            /* @__PURE__ */ jsx14("span", { children: texts.oldGenerator })
          ]
        }
      ) })
    ] }) }),
    topBanner && /* @__PURE__ */ jsx14("div", { className: "lg:hidden order-1", children: topBanner }),
    /* @__PURE__ */ jsxs7("div", { className: "flex flex-col lg:flex-row", children: [
      /* @__PURE__ */ jsx14("div", { className: "flex-1 bg-gray-50 order-2 lg:order-1", children: /* @__PURE__ */ jsxs7("div", { className: "flex justify-center gap-4 lg:gap-8 p-4 lg:p-8", children: [
        leftBanner && /* @__PURE__ */ jsx14("div", { className: "hidden lg:block w-[160px] flex-shrink-0 sticky top-[128px] self-start", children: leftBanner }),
        /* @__PURE__ */ jsx14("div", { className: "flex-1 max-w-4xl", children: /* @__PURE__ */ jsx14(
          PreviewArea,
          {
            text: inputText,
            textConfig,
            backgroundConfig,
            PreviewComponent,
            texts: texts.previewArea
          }
        ) }),
        rightBanner && /* @__PURE__ */ jsx14("div", { className: "hidden lg:block w-[160px] flex-shrink-0 sticky top-[128px] self-start", children: rightBanner })
      ] }) }),
      /* @__PURE__ */ jsx14("div", { className: "w-full lg:min-w-[320px] lg:w-[320px] flex flex-col order-1 lg:order-2 lg:sticky lg:top-[108px] lg:h-[calc(100vh-110px)]", children: /* @__PURE__ */ jsxs7("div", { className: "flex flex-col h-full", children: [
        !!showAIFeatures && /* @__PURE__ */ jsx14("div", { className: "p-4 pb-0 bg-white border-t lg:border-t-0 lg:border-l border-border flex-shrink-0", children: /* @__PURE__ */ jsxs7("div", { className: "grid w-full grid-cols-2 h-10 p-1 bg-muted rounded-md", children: [
          /* @__PURE__ */ jsx14(
            "button",
            {
              onClick: () => setActiveTab("text"),
              className: cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
                activeTab === "text" ? "bg-background text-foreground shadow-sm" : ""
              ),
              children: texts.notesTab
            }
          ),
          /* @__PURE__ */ jsxs7(
            "button",
            {
              onClick: () => setActiveTab("ai"),
              className: cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all relative",
                activeTab === "ai" ? "bg-background text-foreground shadow-sm" : ""
              ),
              children: [
                /* @__PURE__ */ jsxs7("span", { className: "flex items-center gap-1", children: [
                  texts.aiTab,
                  (!isAuthenticated || aiTokens === 0) && /* @__PURE__ */ jsx14(Lock, { size: 12, className: "text-gray-500" })
                ] }),
                /* @__PURE__ */ jsx14("span", { className: "absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-2 flex-shrink-0", children: "NEW" })
              ]
            }
          )
        ] }) }),
        activeTab === "text" && /* @__PURE__ */ jsxs7("div", { className: "flex flex-col flex-1 bg-white lg:border-l border-border", children: [
          /* @__PURE__ */ jsxs7("div", { className: "p-4 border-b border-border flex-shrink-0", children: [
            /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-between mb-2", children: [
              /* @__PURE__ */ jsx14("h3", { className: "font-medium", children: texts.notesText }),
              /* @__PURE__ */ jsxs7("span", { className: "text-xs text-muted-foreground", children: [
                inputText.length,
                " ",
                texts.characters
              ] })
            ] }),
            /* @__PURE__ */ jsxs7(Popover, { open: showRecognitionModal, onOpenChange: setShowRecognitionModal, children: [
              /* @__PURE__ */ jsx14(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs7(Button, { variant: "outline", size: "sm", className: "w-full h-10 lg:h-8", children: [
                /* @__PURE__ */ jsx14(Upload2, { className: "w-5 h-5 lg:w-4 lg:h-4 mr-2" }),
                texts.recognizeText
              ] }) }),
              /* @__PURE__ */ jsx14(PopoverContent, { className: "w-[90vw] max-w-md lg:w-[23rem] z-50 bg-white", children: /* @__PURE__ */ jsx14(
                TextRecognition,
                {
                  onTextExtracted: (text) => {
                    handleTextChange(text);
                    setShowRecognitionModal(false);
                  },
                  onFileUpload: onTextRecognition,
                  texts: texts.textRecognition
                }
              ) })
            ] })
          ] }),
          /* @__PURE__ */ jsx14("div", { className: "flex-1 p-4", children: /* @__PURE__ */ jsx14(
            Textarea,
            {
              placeholder: texts.textPlaceholder,
              value: inputText,
              onChange: (e) => handleTextChange(e.target.value),
              className: "w-full h-full min-h-[200px] lg:min-h-0 resize-none border-0 shadow-none focus:ring-0",
              autoFocus: true
            }
          ) }),
          /* @__PURE__ */ jsx14("div", { className: "px-4 pb-4 flex-shrink-0", children: /* @__PURE__ */ jsxs7(Popover, { children: [
            /* @__PURE__ */ jsx14(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs7(
              Button,
              {
                size: "sm",
                className: "w-full h-10 lg:h-8 bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground",
                children: [
                  /* @__PURE__ */ jsx14(Download2, { className: "w-5 h-5 lg:w-4 lg:h-4 mr-2" }),
                  texts.download
                ]
              }
            ) }),
            /* @__PURE__ */ jsx14(PopoverContent, { className: "w-[90vw] max-w-md lg:w-[23rem] z-50 bg-white", children: /* @__PURE__ */ jsxs7("div", { className: "space-y-2", children: [
              /* @__PURE__ */ jsx14("h4", { className: "font-medium", children: texts.downloadOptions }),
              /* @__PURE__ */ jsxs7("div", { className: "space-y-1", children: [
                showNewFeatures && /* @__PURE__ */ jsx14(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "w-full justify-start h-auto p-3 text-left relative group",
                    onClick: handlePDFClick,
                    children: /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-between w-full", children: [
                      /* @__PURE__ */ jsx14("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs7("div", { children: [
                        /* @__PURE__ */ jsxs7("div", { className: "font-medium flex items-center gap-2", children: [
                          texts.downloadPDF,
                          (!isAuthenticated || !hasSubscription) && /* @__PURE__ */ jsx14(Lock, { size: 14, className: "text-gray-500 group-hover:!text-white" })
                        ] }),
                        /* @__PURE__ */ jsx14("div", { className: "text-xs text-muted-foreground group-hover:text-white", children: texts.downloadPDFDesc })
                      ] }) }),
                      /* @__PURE__ */ jsx14("span", { className: "bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-2 flex-shrink-0", children: "NEW" })
                    ] })
                  }
                ),
                /* @__PURE__ */ jsx14("div", { className: "w-full relative", children: /* @__PURE__ */ jsx14(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "w-full justify-start h-auto p-3 text-left relative group",
                    onClick: onDownloadNormal,
                    disabled: !onDownloadNormal,
                    children: /* @__PURE__ */ jsx14("div", { className: "flex items-center justify-between w-full", children: /* @__PURE__ */ jsx14("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs7("div", { children: [
                      /* @__PURE__ */ jsx14("div", { className: "font-medium flex items-center gap-2", children: texts.downloadNormal }),
                      /* @__PURE__ */ jsx14("div", { className: "text-xs text-muted-foreground group-hover:text-white", children: texts.downloadNormalDesc })
                    ] }) }) })
                  }
                ) }),
                /* @__PURE__ */ jsx14("div", { className: "w-full relative", children: /* @__PURE__ */ jsx14(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "w-full justify-start h-auto p-3 text-left relative group",
                    onClick: onDownloadHighRes,
                    disabled: !onDownloadHighRes,
                    children: /* @__PURE__ */ jsx14("div", { className: "flex items-center justify-between w-full", children: /* @__PURE__ */ jsx14("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs7("div", { children: [
                      /* @__PURE__ */ jsx14("div", { className: "font-medium flex items-center gap-2", children: texts.downloadHighRes }),
                      /* @__PURE__ */ jsx14("div", { className: "text-xs text-muted-foreground group-hover:text-white", children: texts.downloadHighResDesc })
                    ] }) }) })
                  }
                ) })
              ] })
            ] }) })
          ] }) })
        ] }),
        activeTab === "ai" && /* @__PURE__ */ jsx14("div", { className: "flex flex-col flex-1 bg-white lg:border-l border-border overflow-hidden", children: /* @__PURE__ */ jsx14(
          AiChatInterface,
          {
            messages: chatMessages,
            isProcessing: isAIProcessing,
            availableTokens: aiTokens,
            aiActions: aiActions.length > 0 ? aiActions : [{ id: "default", name: "\u041E\u0431\u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C \u0442\u0435\u043A\u0441\u0442" }],
            isFullscreen: isAiChatFullscreen,
            onSetFullscreen: setIsAiChatFullscreen,
            onSendMessage: (text, actionId) => {
              if (!isAuthenticated) {
                setIsAiChatFullscreen(false);
                onShowAuthModal?.();
                return;
              }
              if (aiTokens === 0) {
                setIsAiChatFullscreen(false);
                onShowSubscriptionModal?.("tokens");
                return;
              }
              onSendChatMessage?.(text, actionId);
            },
            className: "flex-1",
            texts: texts.aiChat
          }
        ) })
      ] }) })
    ] })
  ] });
}

// src/components/LoadingOverlay.tsx
import { Loader2 as Loader22 } from "lucide-react";
import { jsx as jsx15, jsxs as jsxs8 } from "react/jsx-runtime";
function LoadingOverlay({ isVisible, message = "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430..." }) {
  if (!isVisible) return null;
  return /* @__PURE__ */ jsx15("div", { className: "fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center", children: /* @__PURE__ */ jsxs8("div", { className: "bg-white rounded-lg shadow-xl p-8 flex flex-col items-center space-y-4", children: [
    /* @__PURE__ */ jsx15(Loader22, { className: "w-8 h-8 animate-spin text-primary" }),
    /* @__PURE__ */ jsx15("p", { className: "text-lg font-medium text-foreground", children: message })
  ] }) });
}

// src/components/PlanCard.tsx
import { Check as Check4 } from "lucide-react";
import { jsx as jsx16, jsxs as jsxs9 } from "react/jsx-runtime";
var ORDERED_FEATURES = ["double_page", "pdf_export", "no_ads"];
function PlanCard({
  plan,
  premiumPlan,
  isLoading,
  featureLabels,
  hasPremium,
  isPopular = false,
  onPlanAction,
  priceDisplay,
  previewMode = false,
  texts
}) {
  const t = texts;
  const isTokenPlan = plan.tokens && plan.tokens > 0;
  const isPremiumPlan = plan.features.includes("pdf_export");
  const totalPrice = plan.price;
  const handleClick = () => {
    onPlanAction(plan.id, false, totalPrice);
  };
  const ownFeatures = ORDERED_FEATURES.filter((f) => plan.features.includes(f));
  return /* @__PURE__ */ jsxs9(Card, { className: `relative flex flex-col hover:shadow-lg transition-shadow ${isPopular ? "ring-2 ring-blue-500 scale-105" : ""}`, children: [
    !!isPopular && /* @__PURE__ */ jsx16("div", { className: "absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium", children: t.popular }),
    /* @__PURE__ */ jsxs9(CardHeader, { className: "text-center", children: [
      /* @__PURE__ */ jsx16(CardTitle, { className: "text-lg", children: plan.name }),
      /* @__PURE__ */ jsx16("div", { className: "text-sm text-muted-foreground", children: t.forDuration(t.getDuration(plan.durationDays)) })
    ] }),
    /* @__PURE__ */ jsxs9(CardContent, { className: "flex flex-col flex-1 space-y-4", children: [
      /* @__PURE__ */ jsxs9("div", { className: "space-y-2 flex-1", children: [
        /* @__PURE__ */ jsx16("p", { className: "text-xs font-medium uppercase tracking-wide text-muted-foreground", children: t.whatsIncluded }),
        /* @__PURE__ */ jsxs9("ul", { className: "space-y-1.5", children: [
          !!isTokenPlan && /* @__PURE__ */ jsxs9("li", { className: "flex items-start gap-2 text-sm", children: [
            /* @__PURE__ */ jsx16(Check4, { className: "h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" }),
            /* @__PURE__ */ jsx16("span", { className: "font-medium", children: t.getTokenDescription(plan.tokens / 1e3) })
          ] }),
          ownFeatures.map((feature) => /* @__PURE__ */ jsxs9("li", { className: "flex items-center gap-2 text-sm", children: [
            /* @__PURE__ */ jsx16(Check4, { className: "h-4 w-4 text-green-500 flex-shrink-0" }),
            featureLabels[feature] || feature
          ] }, feature))
        ] })
      ] }),
      /* @__PURE__ */ jsx16("div", { className: "text-center", children: /* @__PURE__ */ jsx16("div", { className: "text-2xl font-bold", children: priceDisplay ?? `${totalPrice}\u20BD` }) }),
      previewMode ? /* @__PURE__ */ jsx16(Button, { disabled: true, className: "w-full", variant: "outline", size: "lg", children: t.comingSoon }) : /* @__PURE__ */ jsx16(
        Button,
        {
          onClick: handleClick,
          disabled: isLoading,
          className: `w-full ${isPopular ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" : isPremiumPlan ? "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600" : ""}`,
          variant: isPopular || isPremiumPlan ? "default" : "outline",
          size: "lg",
          children: isLoading ? t.processing : t.buy
        }
      )
    ] })
  ] });
}

// src/components/SubscriptionModal.tsx
import { jsx as jsx17, jsxs as jsxs10 } from "react/jsx-runtime";
function SubscriptionModal({
  isOpen,
  onClose,
  onSubscribe,
  onTokenPurchase,
  plans = [],
  loadingPlanId = null,
  featureLabels = {},
  hasPremium = false,
  texts,
  planCardTexts
}) {
  const premiumPlan = plans.find((p) => p.features.includes("pdf_export"));
  const tokenPlans = plans.filter((p) => p.tokens && p.tokens > 0);
  const allDisplayPlans = [...!hasPremium && premiumPlan ? [premiumPlan] : [], ...tokenPlans];
  const handlePlanAction = (planId, includePremium, totalPrice) => {
    const plan = plans.find((p) => p.id === planId);
    if (plan?.tokens && plan.tokens > 0) {
      onTokenPurchase?.(planId, includePremium, totalPrice);
    } else {
      onSubscribe?.(planId);
    }
  };
  return /* @__PURE__ */ jsx17(Dialog, { open: isOpen, onOpenChange: onClose, children: /* @__PURE__ */ jsxs10(DialogContent, { className: "w-[90vw] max-w-4xl max-h-[90vh] overflow-y-auto", children: [
    /* @__PURE__ */ jsx17(DialogHeader, { children: /* @__PURE__ */ jsx17(DialogTitle, { className: "text-2xl", children: texts?.title }) }),
    /* @__PURE__ */ jsx17("div", { className: "space-y-6 py-2", children: /* @__PURE__ */ jsxs10("div", { children: [
      /* @__PURE__ */ jsx17("p", { className: "text-sm font-medium mb-3", children: texts?.choosePlan }),
      /* @__PURE__ */ jsx17("div", { className: `grid gap-4 ${allDisplayPlans.length >= 4 ? "grid-cols-1 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"}`, children: allDisplayPlans.map((plan) => /* @__PURE__ */ jsx17(
        PlanCard,
        {
          plan,
          premiumPlan,
          isLoading: loadingPlanId === plan.id,
          featureLabels,
          hasPremium,
          isPopular: plan.name === "\u0421\u0442\u0430\u043D\u0434\u0430\u0440\u0442",
          onPlanAction: handlePlanAction,
          texts: planCardTexts
        },
        plan.id
      )) })
    ] }) })
  ] }) });
}

// src/components/ProfilePage.tsx
import { useEffect as useEffect3 } from "react";

// src/components/ui/avatar.tsx
import * as React14 from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { jsx as jsx18 } from "react/jsx-runtime";
var Avatar = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx18(
  AvatarPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    ),
    ...props
  }
));
Avatar.displayName = AvatarPrimitive.Root.displayName;
var AvatarImage = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx18(
  AvatarPrimitive.Image,
  {
    ref,
    className: cn("aspect-square h-full w-full", className),
    ...props
  }
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
var AvatarFallback = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx18(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// src/components/ui/badge.tsx
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx19 } from "react/jsx-runtime";
var badgeVariants = cva4(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx19("div", { className: cn(badgeVariants({ variant }), className), ...props });
}

// src/components/ProfilePage.tsx
import { User, CreditCard, Check as Check5 } from "lucide-react";
import { jsx as jsx20, jsxs as jsxs11 } from "react/jsx-runtime";
var ProfilePage = ({
  user,
  currentSubscription,
  hasSubscription,
  isLoading,
  onFetchCurrentSubscription,
  onCancelSubscription,
  onSubscribe,
  texts,
  tokensRemaining = 0,
  tokensPurchased = 0,
  tokensPlanName,
  tokensExpireAt
}) => {
  useEffect3(() => {
    onFetchCurrentSubscription();
  }, [onFetchCurrentSubscription]);
  const handleCancelSubscription = async () => {
    if (currentSubscription?.id && window.confirm(texts?.confirmCancel)) {
      try {
        await onCancelSubscription(currentSubscription.id);
      } catch (error) {
        console.error("Error cancelling subscription:", error);
      }
    }
  };
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(texts?.dateLocale);
  };
  const getFeatureLabel = (feature) => {
    return texts?.featureLabels?.[feature] || feature;
  };
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(" ").map((n) => n.charAt(0)).join("").substring(0, 2).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "?";
  };
  if (!user) {
    return /* @__PURE__ */ jsx20("div", { className: "container mx-auto px-4 py-8 max-w-4xl", children: /* @__PURE__ */ jsx20(Card, { children: /* @__PURE__ */ jsxs11(CardContent, { className: "p-8 text-center", children: [
      /* @__PURE__ */ jsx20("h1", { className: "text-2xl font-bold mb-4", children: texts?.accessDenied }),
      /* @__PURE__ */ jsx20("p", { className: "text-muted-foreground", children: texts?.accessDeniedMessage })
    ] }) }) });
  }
  return /* @__PURE__ */ jsxs11("div", { className: "container mx-auto px-4 py-8 max-w-4xl space-y-6", children: [
    /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-4 mb-8", children: [
      /* @__PURE__ */ jsx20(Avatar, { className: "h-16 w-16", children: /* @__PURE__ */ jsx20(AvatarFallback, { className: "bg-gradient-primary text-primary-foreground text-xl font-bold", children: getUserInitials() }) }),
      /* @__PURE__ */ jsxs11("div", { children: [
        /* @__PURE__ */ jsx20("h1", { className: "text-3xl font-bold", children: user.name || texts?.defaultUserName }),
        /* @__PURE__ */ jsx20("p", { className: "text-muted-foreground", children: user.email })
      ] })
    ] }),
    /* @__PURE__ */ jsxs11(Card, { children: [
      /* @__PURE__ */ jsx20(CardHeader, { children: /* @__PURE__ */ jsxs11(CardTitle, { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx20(User, { className: "h-5 w-5" }),
        texts?.profileSection
      ] }) }),
      /* @__PURE__ */ jsx20(CardContent, { children: /* @__PURE__ */ jsxs11("div", { className: "space-y-3", children: [
        /* @__PURE__ */ jsxs11("div", { className: "flex", children: [
          /* @__PURE__ */ jsx20("span", { className: "text-muted-foreground", children: texts?.nameLabel }),
          /* @__PURE__ */ jsx20("span", { className: "font-medium", children: user.name || texts?.notSet })
        ] }),
        /* @__PURE__ */ jsxs11("div", { className: "flex", children: [
          /* @__PURE__ */ jsx20("span", { className: "text-muted-foreground", children: texts?.emailLabel }),
          /* @__PURE__ */ jsx20("span", { className: "font-medium", children: user.email })
        ] })
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs11(Card, { children: [
      /* @__PURE__ */ jsxs11(CardHeader, { children: [
        /* @__PURE__ */ jsxs11(CardTitle, { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx20(CreditCard, { className: "h-5 w-5" }),
          texts?.subscriptionSection
        ] }),
        /* @__PURE__ */ jsx20(CardDescription, { children: texts?.subscriptionDescription })
      ] }),
      /* @__PURE__ */ jsxs11(CardContent, { children: [
        isLoading ? /* @__PURE__ */ jsx20("div", { className: "flex items-center justify-center py-8", children: /* @__PURE__ */ jsx20("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary" }) }) : hasSubscription && currentSubscription ? /* @__PURE__ */ jsx20("div", { className: "space-y-4", children: /* @__PURE__ */ jsx20("div", { className: "flex items-center justify-between p-4 border rounded-lg", children: /* @__PURE__ */ jsxs11("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx20("h3", { className: "font-semibold text-lg", children: currentSubscription.plan?.name }),
            /* @__PURE__ */ jsx20(Badge, { variant: currentSubscription.status === "active" ? "default" : "secondary", children: currentSubscription.status === "active" ? texts?.activeStatus : texts?.inactiveStatus })
          ] }),
          currentSubscription.plan?.features && /* @__PURE__ */ jsxs11("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx20("p", { className: "text-sm font-medium", children: texts?.includedFeatures }),
            /* @__PURE__ */ jsx20("ul", { className: "space-y-1", children: currentSubscription.plan.features.map((feature, index) => /* @__PURE__ */ jsxs11("li", { className: "flex items-center gap-2 text-sm", children: [
              /* @__PURE__ */ jsx20(Check5, { className: "h-4 w-4 text-green-500 flex-shrink-0" }),
              getFeatureLabel(feature)
            ] }, index)) })
          ] })
        ] }) }) }) : tokensRemaining <= 0 ? /* @__PURE__ */ jsxs11("div", { className: "text-center py-8 space-y-4", children: [
          /* @__PURE__ */ jsxs11("div", { className: "text-muted-foreground", children: [
            /* @__PURE__ */ jsx20(CreditCard, { className: "h-12 w-12 mx-auto mb-4 opacity-50" }),
            /* @__PURE__ */ jsx20("p", { className: "text-lg", children: texts?.noSubscription }),
            /* @__PURE__ */ jsx20("p", { className: "text-sm", children: texts?.noSubscriptionHint })
          ] }),
          /* @__PURE__ */ jsx20(Button, { onClick: onSubscribe, children: texts?.getSubscriptionLink })
        ] }) : null,
        tokensRemaining > 0 && /* @__PURE__ */ jsx20("div", { className: "flex items-center justify-between p-4 border rounded-lg mt-4", children: /* @__PURE__ */ jsxs11("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx20("h3", { className: "font-semibold text-lg", children: tokensPlanName || texts?.tokensSection }),
            /* @__PURE__ */ jsx20(Badge, { variant: "default", children: texts?.activeStatus })
          ] }),
          /* @__PURE__ */ jsx20("div", { className: "space-y-1", children: /* @__PURE__ */ jsxs11("ul", { className: "space-y-1", children: [
            tokensPurchased > 0 && /* @__PURE__ */ jsxs11("li", { className: "flex items-center gap-2 text-sm", children: [
              /* @__PURE__ */ jsx20(Check5, { className: "h-4 w-4 text-green-500 flex-shrink-0" }),
              tokensPurchased.toLocaleString(),
              " ",
              texts?.tokensPurchasedLabel
            ] }),
            tokensExpireAt && /* @__PURE__ */ jsxs11("li", { className: "text-sm text-muted-foreground ml-6", children: [
              texts?.tokensExpire,
              ": ",
              formatDate(tokensExpireAt)
            ] })
          ] }) })
        ] }) })
      ] })
    ] })
  ] });
};

// src/components/UserAvatar.tsx
import { jsx as jsx21, jsxs as jsxs12 } from "react/jsx-runtime";
var UserAvatar = ({
  user,
  size = "sm",
  showName = false,
  onClick,
  className = "",
  defaultUserName
}) => {
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(" ").map((n) => n.charAt(0)).join("").substring(0, 2).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "?";
  };
  const getDisplayName = () => {
    return user?.name || user?.email?.split("@")[0] || (defaultUserName ?? "");
  };
  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12"
  };
  const textSizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base"
  };
  if (!user) {
    return null;
  }
  const avatarElement = /* @__PURE__ */ jsx21(Avatar, { className: `${sizeClasses[size]} ${className}`, children: /* @__PURE__ */ jsx21(AvatarFallback, { className: `bg-gradient-primary text-primary-foreground ${textSizeClasses[size]} font-bold`, children: getUserInitials() }) });
  if (onClick) {
    return /* @__PURE__ */ jsxs12(
      Button,
      {
        variant: "ghost",
        className: `p-2 h-auto gap-2 hover:bg-accent/50 ${showName ? "flex-row" : ""}`,
        onClick,
        children: [
          avatarElement,
          showName && /* @__PURE__ */ jsx21("span", { className: "text-sm font-medium", children: getDisplayName() })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs12("div", { className: `flex items-center gap-2 ${showName ? "flex-row" : ""}`, children: [
    avatarElement,
    showName && /* @__PURE__ */ jsx21("span", { className: "text-sm font-medium", children: getDisplayName() })
  ] });
};

// src/components/PricingPage.tsx
import { jsx as jsx22, jsxs as jsxs13 } from "react/jsx-runtime";
function PricingPage({
  plans,
  featureLabels,
  title,
  subtitle,
  planCardTexts
}) {
  const premiumPlan = plans.find((p) => p.features.includes("pdf_export"));
  return /* @__PURE__ */ jsx22("div", { className: "py-12 px-4", children: /* @__PURE__ */ jsxs13("div", { className: "max-w-5xl mx-auto", children: [
    /* @__PURE__ */ jsx22("h1", { className: "text-3xl font-bold text-center mb-2", children: title }),
    /* @__PURE__ */ jsx22("p", { className: "text-center text-muted-foreground mb-10", children: subtitle }),
    /* @__PURE__ */ jsx22("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6", children: plans.map((plan) => /* @__PURE__ */ jsx22(
      PlanCard,
      {
        plan,
        premiumPlan,
        isLoading: false,
        featureLabels: featureLabels ?? {},
        hasPremium: false,
        isPopular: plan.isPopular,
        onPlanAction: () => {
        },
        priceDisplay: plan.priceDisplay,
        previewMode: true,
        texts: planCardTexts
      },
      plan.id
    )) })
  ] }) });
}
export {
  HandwritingGenerator,
  LoadingOverlay,
  PricingPage,
  ProfilePage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SubscriptionModal,
  UserAvatar
};
